import { mapState, mapActions } from 'vuex';

import { prettyJSONStr } from '@helpers';
import { namespace as MachineCategoriesNamespace } from '@modules/additional/machineCategories/store.types';

const validateMachineName = ({ machines, name, currentName = null }) => {
  const machinesExcludeCurrent = machines.filter(m => m.name !== currentName);
  let errorMessage = '';
  if (!name) {
    errorMessage = 'Name is required!';
  } else if (machinesExcludeCurrent.some(m => m.name === name)) {
    errorMessage = 'This name already used';
  }
  return {
    error: !!errorMessage,
    errorMessages: errorMessage ? [errorMessage] : [],
  };
};

export default {
  name: 'MachineEditor',
  model: {
    type: 'input',
    prop: 'value',
  },
  props: {
    value: Boolean,
    create: Boolean,
    machine: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      name: '',
      shortName: '',
      proxyUrl: '',
      category: '',
      stackerHeight: 1,
      defaultTrayName: '',
      description: '',
    };
  },
  computed: {
    ...mapState('machines', ['machines', 'defaultMachineDescription']),
    ...mapState(MachineCategoriesNamespace, {
      machineCategoriesOptions: ({ machineCategories }) => machineCategories.map(({ name }) => ({
        value: name, text: name,
      })),
    }),
    dialog: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit('input', newValue);
      },
    },
    title() {
      return this.create
        ? 'Create machine'
        : `Edit machine: ${this.machine.name}`;
    },

    nameErrorConfig() {
      const response = { machines: this.machines, name: this.name };
      if (!this.create) {
        response.currentName = this.machine.name;
      }
      return validateMachineName(response);
    },

    stackerHeightErrorConfig() {
      return {
        error: this.stackerHeight < 1,
        messages: this.stackerHeight < 1 ? ['Required, should be greater than 0'] : [],
      };
    },

    categoryErrorConfig() {
      return {
        error: !this.category,
        errorMessages: !this.category ? ['Field must be filled!'] : [],
      };
    },

    isInvalidForm() {
      const categoryError = this.create ? this.categoryErrorConfig.error : false;

      return this.nameErrorConfig.error || categoryError || this.stackerHeightErrorConfig.error;
    },
  },
  watch: {
    value(newValue) {
      if (!newValue || this.create) {
        return;
      }

      const {
        name,
        shortName,
        proxyUrl,
        stackerHeight,
        defaultTrayName,
        description,
      } = this.machine;
      Object.assign(this, {
        name,
        shortName,
        proxyUrl,
        stackerHeight,
        defaultTrayName,
        description: prettyJSONStr(description || this.defaultMachineDescription),
      });
    },
    machineCategoriesOptions: {
      handler() {
        this.$nextTick(this.setInitialMachineCategory);
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    ...mapActions('machines', ['createMachine', 'updateMachine']),
    submitMachineForm() {
      if (this.isInvalidForm) {
        return;
      }

      const action = this.create ? this.createMachine : this.updateMachine;

      this.$VBlackerTheme.alert.warning({
        text: `${this.create ? 'Create' : 'Update'} machine?`,
      }, async () => {
        this.$VBlackerTheme.openLoadingSpinner();
        try {
          await action({
            name: this.name,
            currentName: this.machine.name,
            shortName: this.shortName || null,
            proxyUrl: this.proxyUrl || null,
            stackerHeight: this.stackerHeight,
            defaultTrayName: this.defaultTrayName || null,
            description: this.description || null,
            category: this.category,
          });
          this.$emit('success');
          this.dialog = false;
        } catch (e) {
          this.$VBlackerTheme.notification.error(e);
        } finally {
          this.$VBlackerTheme.closeLoadingSpinner();
        }
      });
    },
    setInitialMachineCategory() {
      const [category = {}] = this.machineCategoriesOptions;
      this.category = category.value || '';
    },
    onModalClose() {
      this.setInitialMachineCategory();
      Object.assign(this, {
        name: '',
        shortName: '',
        proxyUrl: '',
        stackerHeight: 1,
        defaultTrayName: '',
      });
      this.$emit('update:create', true);
    },
  },
};
